/**
 * Chamber flow field, axisymmetric (r, z).
 *
 * Rather than solve Navier-Stokes, the velocity field is prescribed from a
 * stream function. In axisymmetric coordinates
 *
 *     u_z = (1/r) dPsi/dr        u_r = -(1/r) dPsi/dz
 *
 * satisfies continuity identically for any Psi, so the field is exactly
 * mass-conserving by construction - which is the property the species transport
 * solver actually needs. This is the dimensional-reduction step the project's
 * computational strategy calls for: it costs nothing at run time, and it gets
 * the geometry that matters right (gas in through a centred showerhead, out
 * through an annular pump port at the bottom edge).
 *
 * Psi(r, z) = A(r) h(z), with
 *
 *     A(r) = -(w0/2) Rs^2 (1 - exp(-r^2/Rs^2))   smooth centred inlet jet
 *     h(z) = 1 - (1 - z/H)^p                     exhaust weighted to the bottom
 *
 * h(0) = 0 gives no flow through the wafer plane, and A'(r) -> 0 as r -> 0
 * keeps the field regular on the axis.
 */

import { MASS, numberDensity, mtorrToPa, thermalSpeed } from './constants'

/** Molecules per second in one sccm (0 C, 1 atm reference). */
export const SCCM_TO_MOLECULES = 4.477962e17

export interface Geometry {
  /** Chamber radius [m]. */
  radius: number
  /** Chamber height, wafer plane to showerhead [m]. */
  height: number
  /** Showerhead jet radius [m]. */
  showerheadRadius: number
  /** Wafer radius [m]. */
  waferRadius: number
}

export const CHAMBER: Geometry = {
  radius: 0.19,
  height: 0.055,
  showerheadRadius: 0.13,
  waferRadius: 0.15,
}

/** Exhaust concentration exponent - higher pulls the pump port further down. */
const EXHAUST_EXPONENT = 2.5

export interface FlowField {
  /** Inlet centreline velocity [m/s]. */
  w0: number
  /** Gas residence time in the chamber [s]. */
  residenceTime: number
  /** Total feed number flow [molecules/s]. */
  moleculeFlow: number
  /** Total gas number density [m^-3]. */
  gasDensity: number
  ur(r: number, z: number): number
  uz(r: number, z: number): number
  /**
   * The stream function itself, with the 2*pi dropped to match the solver's
   * convention.
   *
   * Face fluxes MUST be taken as differences of this rather than as
   * (face-centre velocity x face area). The field is divergence-free
   * continuously, but point-sampled face velocities are not divergence-free
   * DISCRETELY, and the leftover divergence acts as a spurious source of
   * whatever is being transported. That defect let the chamber destroy about
   * 2.6x more feed gas than entered it, which in turn made gas flow almost
   * inert as a process knob.
   *
   * Differencing psi makes the discrete continuity error exactly zero: the four
   * face fluxes of any cell telescope to nothing, by construction.
   */
  psi(r: number, z: number): number
}

export function buildFlow(opts: {
  geom: Geometry
  flowSccm: number
  pressureMtorr: number
  gasTempK: number
}): FlowField {
  const { geom, flowSccm, pressureMtorr, gasTempK } = opts
  const { height: H, showerheadRadius: Rs } = geom

  const gasDensity = numberDensity(mtorrToPa(pressureMtorr), gasTempK)
  const moleculeFlow = flowSccm * SCCM_TO_MOLECULES
  // Volumetric throughput at chamber conditions.
  const volumetric = moleculeFlow / Math.max(gasDensity, 1)
  // Integral of the inlet profile over the showerhead is exactly pi Rs^2.
  const w0 = volumetric / (Math.PI * Rs * Rs)

  const chamberVolume = Math.PI * geom.radius * geom.radius * H
  const residenceTime = volumetric > 0 ? chamberVolume / volumetric : Infinity

  const hOf = (z: number) => 1 - Math.pow(1 - z / H, EXHAUST_EXPONENT)
  const hPrime = (z: number) =>
    (EXHAUST_EXPONENT / H) * Math.pow(Math.max(1 - z / H, 0), EXHAUST_EXPONENT - 1)

  return {
    w0,
    residenceTime,
    moleculeFlow,
    gasDensity,

    psi(r, z) {
      // A(r) h(z), with A(r) = -(w0/2) Rs^2 (1 - exp(-r^2/Rs^2)).
      return -0.5 * w0 * Rs * Rs * (1 - Math.exp(-(r * r) / (Rs * Rs))) * hOf(z)
    },

    uz(r, z) {
      return -w0 * Math.exp(-(r * r) / (Rs * Rs)) * hOf(z)
    },

    ur(r, z) {
      // Series-expanded near the axis to stay finite as r -> 0.
      const x = (r * r) / (Rs * Rs)
      const shape = x < 1e-4 ? r / 2 : (Rs * Rs * (1 - Math.exp(-x))) / (2 * r)
      return w0 * shape * hPrime(z)
    },
  }
}

/** Mean thermal speed of the etchant radical [m/s]. */
export const etchantThermalSpeed = (tempK: number) => thermalSpeed(tempK, MASS.F)
